/**
 * Plasma Design System — typed JS token exports.
 *
 * Single source of truth for color values in JavaScript/TypeScript contexts:
 * inline styles, placeholderTextColor, React Navigation themes, etc.
 *
 * Hex values mirror plasma-palette.css and plasma-theme-native.css exactly.
 * Use this instead of @plasma/data/color-palette (deprecated).
 */

// ── Semantic colors ────────────────────────────────────────────────────────

export const colors = {
  light: {
    background: "#F6F9FC",
    foreground: "#30313D",
    card: "#FFFFFF",
    cardForeground: "#30313D",
    primary: "#635BFF",
    primaryForeground: "#FFFFFF",
    secondary: "#EEF2F7",
    secondaryForeground: "#3C4257",
    muted: "#EEF2F7",
    mutedForeground: "#6B7385",
    destructive: "#DF1B41",
    destructiveForeground: "#FFFFFF",
    success: "#1A9F58",
    successForeground: "#FFFFFF",
    warning: "#E89A00",
    warningForeground: "#FFFFFF",
    border: "#E3E8EF",
    input: "#D4DEE9",
    ring: "#635BFF",
  },
  dark: {
    background: "#141520",
    foreground: "#F5F6F9",
    card: "#1C2030",
    cardForeground: "#F5F6F9",
    primary: "#7A73FF",
    primaryForeground: "#FFFFFF",
    secondary: "#222838",
    secondaryForeground: "#C8CDE0",
    muted: "#222838",
    mutedForeground: "#8890A8",
    destructive: "#E83A58",
    destructiveForeground: "#FFFFFF",
    success: "#1A9F58",
    successForeground: "#FFFFFF",
    warning: "#E89A00",
    warningForeground: "#FFFFFF",
    border: "#2E3548",
    input: "#2E3548",
    ring: "#7A73FF",
  },
} as const;

// ── Color palette scales ───────────────────────────────────────────────────

export const palette = {
  /** Stripe indigo — primary brand color */
  indigo: {
    50: "#EEEDFF",
    100: "#DAD8FF",
    200: "#B6B2FF",
    300: "#918CFF",
    400: "#7971FF",
    500: "#635BFF",
    600: "#4F49CC",
    700: "#3B3799",
    800: "#272466",
    900: "#131233",
    950: "#090919",
  },
  /** Stripe cool blue-gray — neutral surfaces and text */
  gray: {
    50: "#F6F8FB",
    100: "#EEF1F6",
    200: "#E0E5ED",
    300: "#CAD0DB",
    400: "#96A2B8",
    500: "#687893",
    600: "#4A5568",
    700: "#353E50",
    800: "#1E2537",
    900: "#131923",
    925: "#0D1119",
    950: "#080C12",
  },
  /** Plasma operational green — status indicators */
  green: {
    50: "#E8FCF3",
    100: "#D1FAE8",
    200: "#8CF2C5",
    300: "#8CF2C5",
    400: "#1AE58C",
    500: "#17CF7E",
    600: "#14B870",
    700: "#0F8A54",
    800: "#0A5C38",
    900: "#052E1C",
    950: "#03170E",
  },
} as const;

// ── React Navigation theme helpers ────────────────────────────────────────

export const navigationTheme = {
  light: {
    dark: false,
    colors: {
      primary: colors.light.primary,
      background: colors.light.background,
      card: colors.light.card,
      text: colors.light.foreground,
      border: colors.light.border,
      notification: colors.light.destructive,
    },
  },
  dark: {
    dark: true,
    colors: {
      primary: colors.dark.primary,
      background: colors.dark.background,
      card: colors.dark.card,
      text: colors.dark.foreground,
      border: colors.dark.border,
      notification: colors.dark.destructive,
    },
  },
} as const;

// ── Types ─────────────────────────────────────────────────────────────────

export type ColorScheme = keyof typeof colors;
export type SemanticColor = keyof typeof colors.light;
export type PaletteColor = keyof typeof palette;
